/**
 * Partners route factory. The shell spreads these under the authed route:
 *
 *   children: [...partnersRoutes(authedRoute)]
 *
 * Pages stay router-free (props in, callbacks out); this file is the only
 * place partners code touches TanStack Router, and the only place it reads
 * the session. Every control below is gated on the EXACT permission the
 * generated route policy demands, so no rendered button can 403.
 */
import {
  createRoute,
  useNavigate,
  useParams,
  useSearch,
  type AnyRoute,
} from "@tanstack/react-router";

import { useSession } from "@/lib/session/use-session";

import { PartiesListPage } from "./pages/parties-list-page";
import { PartyDetailPage, PARTY_TABS, type PartyTab } from "./pages/party-detail-page";
import { PaymentTermsPage } from "./pages/payment-terms-page";
import { PartnerGroupsPage } from "./pages/partner-groups-page";
import { PARTNERS_PERMISSIONS as P, PartnersPermissionBoundary } from "./permissions";

export interface PartyDetailSearch {
  /** Active detail tab; the shell keeps it in the URL so tabs are linkable. */
  tab?: PartyTab;
}

export interface PartnerGroupsSearch {
  tab?: "customer" | "supplier";
}

function isPartyTab(value: unknown): value is PartyTab {
  return typeof value === "string" && (PARTY_TABS as readonly string[]).includes(value);
}

function usePartnersNav() {
  const navigate = useNavigate();
  return {
    openList: () => void navigate({ to: "/partners" } as never),
    openCreate: () => void navigate({ to: "/partners/new" } as never),
    openParty: (partyId: string, tab: PartyTab = "customer") =>
      void navigate({
        to: "/partners/$partyId",
        params: { partyId },
        search: { tab },
      } as never),
    openAuditLog: () => void navigate({ to: "/admin/audit-log" } as never),
  };
}

function PartiesListRoute({ createOpen = false }: { createOpen?: boolean }) {
  const nav = usePartnersNav();
  const { hasPermission } = useSession();
  const page = (
    <PartiesListPage
      createOpen={createOpen}
      canCreate={hasPermission(P.partyCreate)}
      // The detail aggregate is the audited PII read; without that grant the
      // row must not navigate into a guaranteed 403.
      canOpenDetail={hasPermission(P.partyPiiRead)}
      onOpenParty={(partyId) => nav.openParty(partyId)}
      onOpenCreate={nav.openCreate}
      onCloseCreate={nav.openList}
    />
  );
  return (
    <PartnersPermissionBoundary permission={P.partyRead}>
      {createOpen ? (
        <PartnersPermissionBoundary permission={P.partyCreate}>{page}</PartnersPermissionBoundary>
      ) : (
        page
      )}
    </PartnersPermissionBoundary>
  );
}

function PartyCreateRoute() {
  return <PartiesListRoute createOpen />;
}

function PartyDetailRoute() {
  const nav = usePartnersNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { partyId?: string };
  const search = useSearch({ strict: false }) as PartyDetailSearch;
  const partyId = params.partyId ?? "";

  return (
    <PartnersPermissionBoundary permission={P.partyPiiRead}>
      <PartyDetailPage
        key={partyId}
        partyId={partyId}
        {...(search.tab === undefined ? {} : { tab: search.tab })}
        onTabChange={(tab) => nav.openParty(partyId, tab)}
        onBack={nav.openList}
        {...(hasPermission("platform.audit.read") ? { onOpenAuditLog: nav.openAuditLog } : {})}
        canUpdate={hasPermission(P.partyUpdate)}
        canArchive={hasPermission(P.partyArchive)}
        canReadCustomerProfile={hasPermission(P.customerProfileRead)}
        canUpdateCustomerProfile={hasPermission(P.customerProfileUpdate)}
        canArchiveCustomerProfile={hasPermission(P.customerProfileArchive)}
        canReadSupplierProfile={hasPermission(P.supplierProfileRead)}
        canUpdateSupplierProfile={hasPermission(P.supplierProfileUpdate)}
        canArchiveSupplierProfile={hasPermission(P.supplierProfileArchive)}
        canReadAddresses={hasPermission(P.addressRead)}
        canCreateAddresses={hasPermission(P.addressCreate)}
        // Editing and "make default" both read the exact address first to
        // capture its strong validator, so they need read as well as update.
        canUpdateAddresses={hasPermission(P.addressRead) && hasPermission(P.addressUpdate)}
        canArchiveAddresses={hasPermission(P.addressArchive)}
        canReadContacts={hasPermission(P.contactRead)}
        canCreateContacts={hasPermission(P.contactCreate)}
        canUpdateContacts={hasPermission(P.contactRead) && hasPermission(P.contactUpdate)}
        canArchiveContacts={hasPermission(P.contactArchive)}
        canReadChannelIdentities={hasPermission(P.channelIdentityRead)}
        canReadCustomerGroups={hasPermission(P.customerGroupRead)}
        canReadSupplierGroups={hasPermission(P.supplierGroupRead)}
        canReadPaymentTerms={hasPermission(P.paymentTermsRead)}
      />
    </PartnersPermissionBoundary>
  );
}

function PaymentTermsRoute() {
  const { hasPermission } = useSession();
  return (
    <PartnersPermissionBoundary permission={P.paymentTermsRead}>
      <PaymentTermsPage
        canCreate={hasPermission(P.paymentTermsCreate)}
        // Edit and restore both prefetch the row's ETag before writing.
        canUpdate={hasPermission(P.paymentTermsRead) && hasPermission(P.paymentTermsUpdate)}
        canArchive={hasPermission(P.paymentTermsArchive)}
      />
    </PartnersPermissionBoundary>
  );
}

function PartnerGroupsRoute() {
  const { hasPermission } = useSession();
  const search = useSearch({ strict: false }) as PartnerGroupsSearch;
  return (
    <PartnersPermissionBoundary permission={P.customerGroupRead}>
      <PartnerGroupsPage
        {...(search.tab === undefined ? {} : { initialTab: search.tab })}
        canReadCustomer={hasPermission(P.customerGroupRead)}
        canCreateCustomer={hasPermission(P.customerGroupCreate)}
        canUpdateCustomer={
          hasPermission(P.customerGroupRead) && hasPermission(P.customerGroupUpdate)
        }
        canArchiveCustomer={hasPermission(P.customerGroupArchive)}
        canReadSupplier={hasPermission(P.supplierGroupRead)}
        canCreateSupplier={hasPermission(P.supplierGroupCreate)}
        canUpdateSupplier={
          hasPermission(P.supplierGroupRead) && hasPermission(P.supplierGroupUpdate)
        }
        canArchiveSupplier={hasPermission(P.supplierGroupArchive)}
      />
    </PartnersPermissionBoundary>
  );
}

/**
 * Route factory per the feature-module contract. Paths follow the partners
 * SPEC's routes table; permissions are the canonical strings from
 * openapi/generated/route_policy.gen.go, which differ from the SPEC's
 * pre-API guesses (see permissions.tsx).
 */
export function partnersRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    createRoute({
      getParentRoute: () => parent,
      path: "/partners",
      component: PartiesListRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/partners/new",
      component: PartyCreateRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/partners/$partyId",
      // Absent keys are OMITTED (never explicit undefined) per router house rules.
      validateSearch: (search: Record<string, unknown>): PartyDetailSearch =>
        isPartyTab(search.tab) ? { tab: search.tab } : {},
      component: PartyDetailRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/settings/payment-terms",
      component: PaymentTermsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/settings/partner-groups",
      validateSearch: (search: Record<string, unknown>): PartnerGroupsSearch =>
        search.tab === "customer" || search.tab === "supplier" ? { tab: search.tab } : {},
      component: PartnerGroupsRoute,
    }),
  ] as AnyRoute[];
}
